import { AccountInfo, IPublicClientApplication } from "@azure/msal-browser";
import { configuration } from "../configuration/ConfigurationService";
import { InteractionStatus } from "@azure/msal-browser";

import { appSessionStorage } from "../storage/SessionStorageService";
import { VIEWER_TOKEN_KEY } from "./AuthService";
import { makeAutoObservable } from "mobx";

export interface Authentication {
  logout: () => void;
  getAccessToken: () => Promise<string>;
  loginAndObtainToken: (
    msalInstance: IPublicClientApplication,
    userAccount: AccountInfo | null,
    viewerAuthToken: string | null,
    loginInProgress: InteractionStatus
  ) => void;
}

export class AuthenticationService implements Authentication {
  private msalInstance: IPublicClientApplication | undefined;
  viewerAuthToken: string | null | undefined;
  userAccount: AccountInfo | null | undefined;
  loginInProgress: InteractionStatus | undefined;
  private resolveCallback: Array<(token: string) => void> = [];
  private needsSetup: boolean = true;
  public anonymous: boolean = true;

  constructor() {
    makeAutoObservable(this)
  }

  loginAndObtainToken = async (
    msalInstance: any,
    userAccount: AccountInfo | null,
    viewerAuthToken: string | null,
    loginInProgress: InteractionStatus
  ) => {
    this.setAuthProperties(msalInstance, userAccount, viewerAuthToken, loginInProgress);

    const accessToken = await this.getAccessToken();
    this.sendAccessToken(accessToken);
  };

  setAnonymous = (anonymous: boolean) => {
    this.anonymous = anonymous;
  }

  private setAuthProperties(
    msalInstance: IPublicClientApplication,
    userAccount: AccountInfo | null,
    viewerAuthToken: string | null,
    loginInProgress: InteractionStatus
  ) {
    this.msalInstance = msalInstance;
    this.userAccount = userAccount;
    this.viewerAuthToken = viewerAuthToken;
    this.loginInProgress = loginInProgress;
    this.needsSetup = false;
    this.anonymous = false;
  }

  getAccessToken = async () => {
    if (this.anonymous) {
      return ""
    }
    if (this.needsSetup) {
      return new Promise<string>((resolve, _) => {
        this.resolveCallback.push(resolve);
      });
    } else {
      if (this.viewerAuthToken) {
        appSessionStorage.setStorageItem(VIEWER_TOKEN_KEY, this.viewerAuthToken as string);
        return this.viewerAuthToken;
      } else {
        this.login();
        const token = await this.getAppAuthenticationToken();
        return token;
      }
    }
  };

  private login = async () => {
    if (
      !this.viewerAuthToken &&
      !this.userAccount &&
      this.loginInProgress === InteractionStatus.None
    ) {
      await this.msalInstance?.loginRedirect();
    }
  };

  private getAppAuthenticationToken = async (): Promise<string> => {
    let config = await configuration.get();
    if (this.userAccount && config.scopes && this.msalInstance) {
      try {
        const response = await this.msalInstance.acquireTokenSilent({
          scopes: config.scopes,
          account: this.userAccount,
        })
        return response.accessToken;
      } catch (error) {
        await this.msalInstance?.loginRedirect();
      }
    }
    return "";
  };

  private sendAccessToken = (token: string) => {
    if (this.resolveCallback && token) {
      this.resolveCallback.forEach((resolve) => resolve(token));
    }
  };

  logout = () => {
    appSessionStorage.clear();
    if (this.msalInstance) this.msalInstance.logout();
  };
}
export const authentication = new AuthenticationService();
